import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import BrandKitDisplay from '../brandkit/BrandKitDisplay';
import ThumbnailNavigation from './ThumbnailNavigation';

// Progress 상태를 i18n 메시지로 변환하는 helper
const getProgressMessage = (t, status, percent) => {
  switch (status) {
    case 'preparing':
      return t('progress.preparing');
    case 'checking':
      return t('progress.checking', { percent: percent || 0 });
    case 'saving':
      return t('progress.saving');
    case 'completed':
      return t('progress.completed');
    default:
      return t('typoViewer.checking');
  }
};

const ColorFontViewer = ({
  tabType, // 'color' | 'font'
  slides = [],
  onModalOpen,
  items = [], // colors 또는 fonts
  checkStatus = '', // colorCheckStatus 또는 fontCheckStatus
  isAnalyzing = false,
  checkProgress = {},
  analyzeFunction, // analyzeAllSlidesColors 또는 analyzeAllSlidesFonts
  showAlertModal, // 알림창 표시 함수
  showConfirmModal, // 확인 모달 함수
  onFontChange, // 폰트 변경 콜백 (index, newValue) => void
  isMobile = false, // 모바일 여부
  loadThumbnailsForSlide // 썸네일 lazy loading 함수
}) => {
  const { t } = useTranslation();
  const [currentThumbnailIndex, setCurrentThumbnailIndex] = useState(0);

  // 썸네일 인덱스 초기화 (PPT 변경 시에만 - slides.length 변경 시)
  useEffect(() => {
    setCurrentThumbnailIndex(0);
  }, [slides.length]);

  // 인덱스 변경 핸들러 (lazy loading 포함)
  const handleThumbnailIndexChange = useCallback((newIndex) => {
    setCurrentThumbnailIndex(newIndex);
    // 썸네일 lazy loading 호출
    if (loadThumbnailsForSlide) {
      loadThumbnailsForSlide(newIndex);
    }
  }, [loadThumbnailsForSlide]);

  // 타입별 설정
  const config = {
    color: {
      title: t('colorFontViewer.color.title'),
      buttonText: t('colorFontViewer.color.buttonText'),
      checkingText: '',
      itemsLabel: t('colorFontViewer.color.itemsLabel'),
      successText: t('colorFontViewer.color.successText'),
      violationText: t('colorFontViewer.color.violationText'),
      brandkitChangedTitle: t('colorFontViewer.color.brandkitChangedTitle'),
      brandkitChangedDesc: t('colorFontViewer.color.brandkitChangedDesc'),
      recheckText: t('colorFontViewer.color.recheckText'),
      complianceText: t('colorFontViewer.color.complianceText')
    },
    font: {
      title: t('colorFontViewer.font.title'),
      buttonText: t('colorFontViewer.font.buttonText'),
      checkingText: '',
      itemsLabel: t('colorFontViewer.font.itemsLabel'),
      successText: t('colorFontViewer.font.successText'),
      violationText: t('colorFontViewer.font.violationText'),
      brandkitChangedTitle: t('colorFontViewer.font.brandkitChangedTitle'),
      brandkitChangedDesc: t('colorFontViewer.font.brandkitChangedDesc'),
      recheckText: t('colorFontViewer.font.recheckText'),
      complianceText: t('colorFontViewer.font.complianceText')
    }
  };

  const currentConfig = config[tabType] || config.color;

  return (
    <div style={{
      padding: isMobile ? '12px' : '24px',
      background: '#f5f7fa',
      minHeight: '100vh',
      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
      position: 'relative',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden'
    }}>
      {/* CSS 애니메이션 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `
      }} />

      {/* 상단 레이아웃 - PC: 좌우(2:3), 모바일: 상하 */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '16px' : '24px',
        width: '100%'
      }}>
        {/* 브랜드키트 표시 (PC: flex 2, 모바일: 전체 너비) */}
        <BrandKitDisplay
          type={tabType}
          items={items}
          isMobile={isMobile}
          onFontChange={onFontChange}
          showConfirmModal={showConfirmModal}
        />

        {/* 썸네일 네비게이션 (PC: flex 3, 모바일: 전체 너비) */}
        {slides && slides.length > 0 && (
          <ThumbnailNavigation
            slides={slides}
            onThumbnailClick={null}
            currentThumbnailIndex={currentThumbnailIndex}
            onThumbnailIndexChange={handleThumbnailIndexChange}
            disableClick={true}
            isMobile={isMobile}
          />
        )}
      </div>

      {/* 하단 검사 결과 영역 - 주석처리 (모달로 대체) */}
      {/* {(checkStatus === 'completed' || checkStatus === 'no_violations') && allSlidesResults.length > 0 && (
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e5e7eb',
          marginTop: '24px'
        }}>
            <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '20px',
            textAlign: 'center',
            color: checkStatus === 'no_violations' ? '#059669' : '#dc2626'
          }}>
            {checkStatus === 'no_violations'
              ? currentConfig.successText
              : `${currentConfig.violationText} (${allSlidesResults.filter(r => r.has_violations).length}개 슬라이드 위반)`}
          </h3>

          <div style={{
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151'
              }}>
                슬라이드 {currentThumbnailIndex + 1} 검사 결과
              </div>
              {getCurrentSlideResult(currentThumbnailIndex) && (
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  background: getCurrentSlideResult(currentThumbnailIndex).has_violations
                    ? '#fef2f2'
                    : '#f0fdf4',
                  border: getCurrentSlideResult(currentThumbnailIndex).has_violations
                    ? '1px solid #fecaca'
                    : '1px solid #bbf7d0',
                  color: getCurrentSlideResult(currentThumbnailIndex).has_violations
                    ? '#dc2626'
                    : '#059669'
                }}>
                  {getCurrentSlideResult(currentThumbnailIndex).has_violations
                    ? `⚠️ ${getCurrentSlideResult(currentThumbnailIndex).violations_count}개 위반`
                    : '✅ 규정 준수'}
                </div>
              )}
            </div>

            {getCurrentSlideResult(currentThumbnailIndex) ? (
              <div style={{
                display: 'inline-block',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                backgroundColor: '#fff',
                position: 'relative',
                cursor: 'pointer'
              }} onClick={() => {
                console.log('[FONT MODAL DEBUG] TabView thumbnail click - currentThumbnailIndex:', currentThumbnailIndex, 'tabType:', tabType);
                if (tabType === 'font' && onModalOpen) {
                  onModalOpen(currentThumbnailIndex);
                } else {
                  handleResultThumbnailClick(currentThumbnailIndex, onModalOpen);
                }
              }}>
                <img
                  src={getCurrentSlideResult(currentThumbnailIndex).marked_thumbnail_url || slides[currentThumbnailIndex]?.thumbnail_url}
                  alt={`슬라이드 ${currentThumbnailIndex + 1} 검사 결과`}
                  style={{
                    width: '450px',
                    height: '320px',
                    objectFit: 'contain',
                    backgroundColor: 'transparent',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.01)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                  }}
                />

                {getCurrentSlideResult(currentThumbnailIndex).has_violations ? (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: '#dc2626',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    ⚠️ {getCurrentSlideResult(currentThumbnailIndex).violations_count}개 위반
                  </div>
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: '#059669',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {isColorType ? '✅ 색상 규정 준수' : '✅ 폰트 규정 준수'}
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '400',
                      marginTop: '4px',
                      opacity: 0.9
                    }}>
                      {currentConfig.complianceText}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                padding: '24px',
                color: '#6b7280',
                fontSize: '14px'
              }}>
                이 슬라이드는 아직 검사되지 않았습니다.
              </div>
            )}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: '8px',
            marginTop: '20px'
          }}>
            {slides.map((slide, index) => {
              const result = getCurrentSlideResult(index);
              return (
                <div
                  key={index}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    background: result
                      ? (result.has_violations ? '#fef2f2' : '#f0fdf4')
                      : '#f9fafb',
                    border: result
                      ? (result.has_violations ? '1px solid #fecaca' : '1px solid #bbf7d0')
                      : '1px solid #e5e7eb',
                    color: result
                      ? (result.has_violations ? '#dc2626' : '#059669')
                      : '#6b7280'
                  }}
                  onClick={() => setCurrentThumbnailIndex(index)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div>슬라이드 {index + 1}</div>
                  {result && (
                    <div style={{ marginTop: '4px', fontSize: '10px' }}>
                      {result.has_violations
                        ? `${result.violations_count}개 위반`
                        : '규정 준수'}
                    </div>
                  )}
                  {!result && (
                    <div style={{ marginTop: '4px', fontSize: '10px' }}>
                      미검사
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )} */}

      {/* 브랜드키트 변경으로 인한 재검사 필요 메시지 - 주석처리 (확인버튼 클릭시 알림으로 대체) */}
      {/* {checkStatus === 'outdated' && allSlidesResults.length > 0 && (
        <div style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
          marginTop: '24px'
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '12px',
            color: '#ea580c'
          }}>
            {currentConfig.brandkitChangedTitle}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '16px',
            lineHeight: '1.6'
          }}>
            {currentConfig.brandkitChangedDesc.split('\n').map((line, idx) => (
              <div key={idx}>{line}{idx < currentConfig.brandkitChangedDesc.split('\n').length - 1 && <br/>}</div>
            ))}
          </div>
          <button
            onClick={() => {
              console.log(`[DEBUG] ${tabType} 재검사 버튼 클릭됨 (브랜드키트 변경됨)`);
              console.log('[DEBUG] analyzeFunction 존재 여부:', typeof analyzeFunction);
              if (analyzeFunction) {
                analyzeFunction();
              } else {
                console.error('[ERROR] analyzeFunction이 undefined입니다');
              }
            }}
            disabled={isAnalyzing}
            style={{
              background: isAnalyzing ? '#9ca3af' : '#ea580c',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: isAnalyzing ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {isAnalyzing ? '검사 중...' : currentConfig.recheckText}
          </button>
        </div>
      )} */}

      {/* 확인 버튼 + 전체 슬라이드 검사 버튼 - 우측 하단 고정 */}
      {slides && slides.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: isMobile ? '16px' : '32px',
          right: isMobile ? '16px' : '32px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '12px',
          zIndex: 100
        }}>
          {/* 확인 버튼 - 검사 완료/no_violations/outdated 상태일 때만 표시 (왼쪽) */}
          {(checkStatus === 'completed' || checkStatus === 'no_violations' || checkStatus === 'outdated') && (
            <button
              onClick={() => {
                // 브랜드키트가 변경된 경우 알림창 표시
                if (checkStatus === 'outdated' && showAlertModal) {
                  showAlertModal(t('colorFontViewer.outdatedWarning'), 'warning');
                }
                // 모달 열기
                if (onModalOpen) {
                  onModalOpen(0);
                }
              }}
              style={{
                background: '#ffffff',
                color: '#1A62EA',
                border: '2px solid #1A62EA',
                borderRadius: isMobile ? '12px' : '16px',
                padding: isMobile ? '12px 16px' : '16px 24px',
                cursor: 'pointer',
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '6px' : '10px',
                boxShadow: '0 8px 24px rgba(26, 98, 234, 0.2)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(26, 98, 234, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(26, 98, 234, 0.2)';
              }}
            >
              {t('colorFontViewer.viewResults')}
            </button>
          )}

          {/* 검사 버튼 (오른쪽) */}
          <button
            onClick={() => {
              console.log(`[DEBUG] ${tabType} 검사 버튼 클릭됨`);
              console.log('[DEBUG] analyzeFunction 존재 여부:', typeof analyzeFunction);
              if (analyzeFunction) {
                analyzeFunction();
              } else {
                console.error('[ERROR] analyzeFunction이 undefined입니다');
              }
            }}
            disabled={isAnalyzing || (checkStatus === 'completed' || checkStatus === 'no_violations')}
            style={{
              background: (checkStatus === 'completed' || checkStatus === 'no_violations')
                ? '#9ca3af'
                : isAnalyzing
                  ? '#9ca3af'
                  : checkStatus === 'outdated'
                    ? '#ea580c'
                    : '#1A62EA',
              color: 'white',
              border: 'none',
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '12px 16px' : '16px 24px',
              cursor: (isAnalyzing || (checkStatus === 'completed' || checkStatus === 'no_violations')) ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '6px' : '10px',
              boxShadow: (checkStatus === 'completed' || checkStatus === 'no_violations')
                ? '0 4px 15px rgba(156, 163, 175, 0.4)'
                : checkStatus === 'outdated'
                  ? '0 8px 24px rgba(234, 88, 12, 0.4)'
                  : '0 8px 24px rgba(26, 98, 234, 0.4)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isAnalyzing && !(checkStatus === 'completed' || checkStatus === 'no_violations')) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = checkStatus === 'outdated'
                  ? '0 12px 32px rgba(234, 88, 12, 0.5)'
                  : '0 12px 32px rgba(26, 98, 234, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isAnalyzing && !(checkStatus === 'completed' || checkStatus === 'no_violations')) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = checkStatus === 'outdated'
                  ? '0 8px 24px rgba(234, 88, 12, 0.4)'
                  : '0 8px 24px rgba(26, 98, 234, 0.4)';
              }
            }}
          >
            {(checkStatus === 'completed' || checkStatus === 'no_violations') ? (
              t('colorFontViewer.checkComplete')
            ) : isAnalyzing ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                {getProgressMessage(t, checkProgress.status, checkProgress.percent)}
              </>
            ) : checkStatus === 'outdated' ? (
              currentConfig.recheckText
            ) : (
              currentConfig.buttonText
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ColorFontViewer;